/**
 * SquareGameStyleScoreboard — LED scoreboard hero section for GridEngine.
 * Extracted from SquareGameStyleScoreboard.jsx layout.
 * 
 * Theme-var driven throughout: every accent flows from --charity-primary / --charity-gradient-*
 * so it adapts to any charity palette rather than hardcoding cyan/red.
 */
import React from 'react'
import HeroSiteLogo from '../../../components/HeroSiteLogo'
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import Clock from 'lucide-react/dist/esm/icons/clock.js';
import Grid3X3 from 'lucide-react/dist/esm/icons/grid-3x3.js';
import Radio from 'lucide-react/dist/esm/icons/radio.js';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit'
import CountdownTimerStyles from '../../../components/CountdownTimerStyles'
import HeroPartnerGlassSelect from '../../../components/HeroPartnerGlassSelect'
import SquareBoardPreview from '../../../components/SquareBoardPreview'
import { useContent } from '../../../contexts/ContentContext'
import { usePurchaseNavigate } from '../../../hooks/usePurchaseNavigate'
import { formatCurrencyValue } from '../../../utils/formatCurrency'

/**
 * Scoreboard Hero Section — LED dot-matrix scoreboard theme with scanline animation
 * Dark background with radial-gradient LED dots, flicker animation, and bezel header
 */
export function SquareScoreboardHeroSection({
  mainDraw,
  squares,
  partner,
  t,
  editMode,
  handleTranslationUpdate,
  onLoginClick,
  salesClosed,
  timeLeft,
  shouldAnimate,
  countdownStyle,
  purchaseNavigate,
  language,
}) {
  const { c } = useContent()
  const navigate = purchaseNavigate || usePurchaseNavigate(onLoginClick)

  const grandPrize = mainDraw?.current_jackpot ?? 0
  const totalSquares = mainDraw?.config?.gridSize || mainDraw?.total_tickets || 100
  const ticketsSold = mainDraw?.total_tickets_sold ?? 0
  const filledCount = Math.min(ticketsSold, totalSquares)
  const ticketPrice = mainDraw?.ticket_price ?? 5

  const gamePhase = salesClosed
    ? { key: 'squareScoreboard.phaseLive', fallback: 'GAME LIVE' }
    : filledCount >= totalSquares
    ? { key: 'squareScoreboard.phaseSoldOut', fallback: 'SOLD OUT' }
    : { key: 'squareScoreboard.phaseSalesOpen', fallback: 'SALES OPEN' }

  const quarter = (() => {
    if (totalSquares <= 0) return 'Q1'
    const pct = filledCount / totalSquares
    if (pct < 0.25) return 'Q1'
    if (pct < 0.5) return 'Q2'
    if (pct < 0.75) return 'Q3'
    return 'Q4'
  })()

  return (
    <div
      className="overflow-hidden bg-gray-950 dark:bg-black"
      style={{
        backgroundImage:
          'linear-gradient(135deg, color-mix(in oklab, var(--charity-gradient-start) 20%, #030712), color-mix(in oklab, var(--charity-gradient-end) 20%, #000000))',
      }}
    >
      <style>{`
        @keyframes scoreboard-scanline {
          0%   { transform: translateY(-100%); }
          100% { transform: translateY(100%); }
        }
        @keyframes scoreboard-flicker {
          0%, 100% { opacity: 1; }
          48%      { opacity: 1; }
          50%      { opacity: 0.82; }
          52%      { opacity: 1; }
        }
        .scoreboard-digit {
          font-variant-numeric: tabular-nums;
          letter-spacing: 0.05em;
          text-shadow:
            0 0 8px  color-mix(in oklab, var(--charity-primary) 70%, transparent),
            0 0 24px color-mix(in oklab, var(--charity-primary) 45%, transparent);
          animation: scoreboard-flicker 4s ease-in-out infinite;
        }
        .scoreboard-led {
          background-image:
            radial-gradient(
              circle at center,
              color-mix(in oklab, var(--charity-primary) 18%, transparent) 0 2px,
              transparent 2.5px
            );
          background-size: 6px 6px;
        }
        .scoreboard-scanlines::after {
          content: '';
          position: absolute; inset: 0;
          background: repeating-linear-gradient(
            to bottom,
            rgba(255,255,255,0.04) 0 1px,
            transparent 1px 3px
          );
          pointer-events: none;
        }
      `}</style>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <HeroSiteLogo tone="dark" />
        {/* Partner logo */}
        {partner?.partner_logo_url && (
          <div className="flex justify-center mb-8">
            <img
              src={partner.partner_logo_url}
              alt={partner.partner_name || 'Partner'}
              className="h-14 sm:h-16 object-contain opacity-90"
            />
          </div>
        )}

        <HeroPartnerGlassSelect className="mx-auto mb-8 w-full max-w-md" />

        {/* Main scoreboard panel */}
        <div
          className="scoreboard-scanlines relative rounded-3xl border-2 shadow-2xl overflow-hidden"
          style={{
            backgroundColor: 'color-mix(in oklab, var(--charity-primary) 4%, #050505)',
            borderColor: 'color-mix(in oklab, var(--charity-primary) 55%, transparent)',
            boxShadow:
              '0 0 40px color-mix(in oklab, var(--charity-primary) 30%, transparent), inset 0 0 60px rgba(0,0,0,0.6)',
          }}
        >
          {/* Bezel header strip */}
          <div
            className="flex items-center justify-between px-4 sm:px-6 py-3 border-b"
            style={{
              backgroundColor: 'color-mix(in oklab, var(--charity-primary) 10%, #000)',
              borderColor: 'color-mix(in oklab, var(--charity-primary) 35%, transparent)',
            }}
          >
            <div className="flex items-center gap-2 text-white/80 text-xs sm:text-sm font-mono uppercase tracking-[0.2em]">
              <Radio className="w-4 h-4" style={{ color: 'var(--charity-primary)' }} />
              <InlineTranslationEdit
                translationKey="squareScoreboard.liveLabel"
                value={c('squareScoreboard.liveLabel', 'On The Board')}
                fallback="On The Board"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </div>
            <div className="flex items-center gap-2 text-white/80 text-xs sm:text-sm font-mono uppercase tracking-[0.2em]">
              <span>{quarter}</span>
              <span className="opacity-50">•</span>
              <span>{mainDraw?.name ? String(mainDraw.name).toUpperCase() : 'SQUARE GAME'}</span>
            </div>
          </div>

          {/* Dot-matrix jackpot */}
          <div className="scoreboard-led relative px-4 sm:px-8 py-10 sm:py-14 text-center">
            <p
              className="text-xs sm:text-sm font-mono uppercase tracking-[0.35em] mb-3"
              style={{ color: 'color-mix(in oklab, var(--charity-primary) 85%, #fff)' }}
            >
              <Trophy className="w-4 h-4 inline -mt-1 mr-2" />
              <InlineTranslationEdit
                translationKey="squareScoreboard.grandPrize"
                value={c('squareScoreboard.grandPrize', 'Grand Prize')}
                fallback="Grand Prize"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </p>
            <p
              className="scoreboard-digit text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-black leading-none"
              style={{ color: 'var(--charity-primary)' }}
            >
              {formatCurrencyValue(grandPrize, { language })}
            </p>

            {/* Game phase */}
            <div className="mt-6 flex items-center justify-center gap-3">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{
                  backgroundColor: 'var(--charity-primary)',
                  boxShadow: '0 0 12px var(--charity-primary)',
                }}
              />
              <span
                className="font-mono text-sm sm:text-base uppercase tracking-[0.3em]"
                style={{ color: 'color-mix(in oklab, var(--charity-primary) 75%, #fff)' }}
              >
                <InlineTranslationEdit
                  translationKey="squareScoreboard.gamePhaseLabel"
                  value={c('squareScoreboard.gamePhaseLabel', 'Game Phase:')}
                  fallback="Game Phase:"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </span>
              <span className="font-mono text-sm sm:text-base font-bold text-white tracking-[0.3em]">
                <InlineTranslationEdit
                  translationKey={gamePhase.key}
                  value={c(gamePhase.key, gamePhase.fallback)}
                  fallback={gamePhase.fallback}
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </span>
            </div>

            {/* Animated scan overlay */}
            <div
              className="absolute left-0 right-0 h-24 pointer-events-none opacity-60"
              style={{
                top: 0,
                backgroundImage:
                  'linear-gradient(to bottom, transparent, color-mix(in oklab, var(--charity-primary) 18%, transparent), transparent)',
                animation: 'scoreboard-scanline 6s linear infinite',
              }}
              aria-hidden="true"
            />
          </div>

          {/* Tile board — 10 cols on sm+, horizontal scroll on xs */}
          <SquareBoardPreview
            totalSquares={totalSquares}
            squares={squares}
            ticketsSold={ticketsSold}
            heading={c('squareScoreboard.boardLabel', 'Board')}
            onSquareClick={(num) => navigate(`/square-game-purchase?square=${num}`)}
            size="sm"
          />

          {/* Game clock countdown */}
          {mainDraw?.draw_date && (
            <div
              className="px-4 sm:px-8 py-8 border-t text-center"
              style={{
                backgroundColor: 'rgba(0,0,0,0.55)',
                borderColor: 'color-mix(in oklab, var(--charity-primary) 25%, transparent)',
              }}
            >
              <div className="flex items-center justify-center gap-2 mb-4 text-white/70 font-mono text-xs uppercase tracking-[0.3em]">
                <Clock className="w-4 h-4" style={{ color: 'var(--charity-primary)' }} />
                <InlineTranslationEdit
                  translationKey="squareScoreboard.gameClock"
                  value={c('squareScoreboard.gameClock', 'Game Clock')}
                  fallback="Game Clock"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </div>
              <CountdownTimerStyles
                timeLeft={timeLeft}
                shouldAnimate={shouldAnimate}
                style={countdownStyle}
              />
            </div>
          )}

          {/* CTA strip */}
          <div
            className="px-4 sm:px-8 py-6 border-t flex flex-col sm:flex-row items-center justify-between gap-4"
            style={{
              backgroundColor: 'color-mix(in oklab, var(--charity-primary) 8%, #000)',
              borderColor: 'color-mix(in oklab, var(--charity-primary) 30%, transparent)',
            }}
          >
            <div className="text-white/80 font-mono text-sm">
              <span className="opacity-60 uppercase tracking-[0.25em] mr-2">
                <InlineTranslationEdit
                  translationKey="squareScoreboard.perSquare"
                  value={c('squareScoreboard.perSquare', 'Per Square')}
                  fallback="Per Square"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </span>
              <span className="text-white font-bold text-lg">${ticketPrice}</span>
            </div>
            <button
              onClick={() => navigate('/purchase')}
              disabled={salesClosed}
              className="group inline-flex items-center gap-3 px-8 py-3 rounded-full font-black text-base sm:text-lg uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: 'var(--charity-primary)',
                color: 'var(--charity-primary-contrast, #fff)',
                boxShadow: '0 0 24px color-mix(in oklab, var(--charity-primary) 55%, transparent)',
              }}
            >
              <Ticket className="w-5 h-5" />
              <InlineTranslationEdit
                translationKey="squareScoreboard.claimSquare"
                value={c('squareScoreboard.claimSquare', 'Claim Your Square')}
                fallback="Claim Your Square"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
